import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAuth } from '@/hooks/useAuth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AIFeedbackStats } from '@/components/admin/AIFeedbackStats';
import { UserStatsDashboard } from '@/components/admin/UserStatsDashboard';
import { ControlRoomKPIs } from '@/components/admin/ControlRoomKPIs';
import { ClientTrafficLightTable, ClientWithTrafficLight } from '@/components/admin/ClientTrafficLightTable';
import { ClientDetailDialog } from '@/components/admin/ClientDetailDialog';
import { AdminCreateUserDialog } from '@/components/admin/AdminCreateUserDialog';
import { fetchClientsWithTrafficLights, generateClientAlerts } from '@/hooks/useClientAlerts';
import { supabase } from '@/integrations/supabase/client';

export default function Admin() {
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientWithTrafficLight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<ClientWithTrafficLight | null>(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [criticalAlerts, setCriticalAlerts] = useState(0);
  const [pendingTasks, setPendingTasks] = useState(0);

  useEffect(() => {
    if (!loading && !isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, loading, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const clientsData = await fetchClientsWithTrafficLights();
      setClients(clientsData);

      // Generate alerts for each client
      for (const client of clientsData) {
        await generateClientAlerts({
          id: client.id,
          name: client.name,
          email: client.email,
          created_at: client.created_at,
          is_active: client.is_active,
          suspended_until: client.suspended_until,
          access_expires_at: client.access_expires_at,
          suspension_reason: client.suspension_reason,
          latestSummary: client.latestSummary,
          goalsCount: client.goalsCount,
          transactionsCount: client.transactionsCount,
          lastTransactionDate: null,
        });
      }

      // Fetch critical alerts count
      const { count: alertsCount } = await supabase
        .from('client_alerts')
        .select('*', { count: 'exact', head: true })
        .eq('is_resolved', false)
        .eq('severity', 'critical');
      setCriticalAlerts(alertsCount || 0);

      // Fetch pending tasks count
      const { count: tasksCount } = await supabase
        .from('client_tasks')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');
      setPendingTasks(tasksCount || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewClient = (client: ClientWithTrafficLight) => {
    setSelectedClient(client);
    setShowClientDetail(true);
  };

  // Stats
  const totalClients = clients.length;
  const avgFocoScore = clients.reduce((acc, c) => acc + (c.latestSummary?.foco_score || 0), 0) / (totalClients || 1);
  const clientsWithData = clients.filter(c => c.latestSummary).length;
  const totalGoals = clients.reduce((acc, c) => acc + c.goalsCount, 0);

  if (loading || isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </AppLayout>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <AppLayout>
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold">Control Room</h1>
            <p className="text-muted-foreground mt-1">
              Panel de control CRM para mentoría financiera
            </p>
          </div>
          <AdminCreateUserDialog onCreated={fetchData} />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="control-room" className="space-y-6">
          <TabsList>
            <TabsTrigger value="control-room">Control Room</TabsTrigger>
            <TabsTrigger value="stats">Estadísticas</TabsTrigger>
            <TabsTrigger value="ai-feedback">Feedback IA</TabsTrigger>
          </TabsList>

          <TabsContent value="control-room" className="space-y-6">
            {/* KPIs */}
            <ControlRoomKPIs
              totalClients={totalClients}
              avgFocoScore={avgFocoScore}
              clientsWithData={clientsWithData}
              totalGoals={totalGoals}
              criticalAlerts={criticalAlerts}
              pendingTasks={pendingTasks}
            />

            {/* Client Traffic Light Table */}
            <ClientTrafficLightTable
              clients={clients}
              onViewClient={handleViewClient}
            />
          </TabsContent>

          <TabsContent value="stats">
            <UserStatsDashboard />
          </TabsContent>

          <TabsContent value="ai-feedback">
            <AIFeedbackStats />
          </TabsContent>
        </Tabs>
      </div>

      {/* Client Detail Dialog */}
      <ClientDetailDialog
        client={selectedClient}
        open={showClientDetail}
        onOpenChange={setShowClientDetail}
        onUpdate={fetchData}
      />
    </AppLayout>
  );
}
